import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Eye, EyeOff, Loader2, Trash2, Upload } from "lucide-react";
import { api, apiError } from "@/lib/api";
import { formatDate, LICENSE_STATUS } from "@/lib/format";
import { AdminCard, AdminModal, BulkDeleteBar, Field, Pill, SelectCheckbox, TableWrap, Td, Th, inputCls, textareaCls, useBulkDelete } from "@/pages/admin/ui";

const STATUSES = ["available", "reserved", "sold", "cancelled"];

export default function AdminLicenses() {
  const [items, setItems] = useState([]);
  const [products, setProducts] = useState([]);
  const [filters, setFilters] = useState({ product_id: "", status: "", q: "" });
  const [reveal, setReveal] = useState(false);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [importForm, setImportForm] = useState({ product_id: "", keys: "", note: "" });
  const [importing, setImporting] = useState(false);
  const [total, setTotal] = useState(0);

  const load = async () => {
    setLoading(true);
    try {
      const { data } = await api.get("/admin/licenses", { params: { ...filters, reveal, limit: 100 } });
      setItems(data.items);
      setTotal(data.total);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    api.get("/admin/products", { params: { limit: 200 } }).then(({ data }) => {
      setProducts(data.items);
      setImportForm((f) => ({ ...f, product_id: f.product_id || data.items[0]?.id || "" }));
    });
  }, []);

  useEffect(() => {
    load();
  }, [filters, reveal]);

  const bulk = useBulkDelete("/admin/licenses/bulk-delete", load, "clé(s)");

  const submitImport = async (event) => {
    event.preventDefault();
    setImporting(true);
    try {
      const { data } = await api.post("/admin/licenses/bulk", importForm);
      toast.success(`${data.imported} clé(s) importée(s), ${data.skipped} doublon(s) ignoré(s)`);
      setImportForm((f) => ({ ...f, keys: "", note: "" }));
      setOpen(false);
      load();
    } catch (error) {
      toast.error(apiError(error));
    } finally {
      setImporting(false);
    }
  };

  const changeStatus = async (license, status) => {
    try {
      await api.put(`/admin/licenses/${license.id}`, { status });
      toast.success("Statut mis à jour");
      load();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  const remove = async (license) => {
    if (!window.confirm("Supprimer cette licence ?")) return;
    try {
      await api.delete(`/admin/licenses/${license.id}`);
      load();
    } catch (error) {
      toast.error(apiError(error));
    }
  };

  return (
    <AdminCard
      title="Licences & clés"
      description={`${total} clé(s) enregistrée(s)`}
      action={
        <div className="flex flex-wrap items-center gap-2">
          <button
            data-testid="admin-reveal-keys"
            onClick={() => setReveal((v) => !v)}
            className="flex items-center gap-1.5 rounded-lg border border-line px-3 py-2.5 text-xs font-bold text-ink hover:border-brand hover:text-brand"
          >
            {reveal ? <EyeOff className="h-3.5 w-3.5" /> : <Eye className="h-3.5 w-3.5" />}
            {reveal ? "Masquer les clés" : "Afficher les clés"}
          </button>
          <button
            data-testid="admin-import-licenses"
            onClick={() => setOpen(true)}
            className="flex items-center gap-1.5 rounded-lg bg-brand px-4 py-2.5 text-xs font-bold text-white hover:bg-brand-dark"
          >
            <Upload className="h-3.5 w-3.5" /> Importer des clés
          </button>
        </div>
      }
    >
      <div className="mb-4 grid gap-3 sm:grid-cols-3">
        <select
          data-testid="license-filter-product"
          value={filters.product_id}
          onChange={(e) => setFilters((f) => ({ ...f, product_id: e.target.value }))}
          className={inputCls}
        >
          <option value="">Tous les produits</option>
          {products.map((p) => (
            <option key={p.id} value={p.id}>{p.name}</option>
          ))}
        </select>
        <select
          data-testid="license-filter-status"
          value={filters.status}
          onChange={(e) => setFilters((f) => ({ ...f, status: e.target.value }))}
          className={inputCls}
        >
          <option value="">Tous les statuts</option>
          {STATUSES.map((s) => (
            <option key={s} value={s}>{LICENSE_STATUS[s].label}</option>
          ))}
        </select>
        <input
          data-testid="license-filter-search"
          value={filters.q}
          onChange={(e) => setFilters((f) => ({ ...f, q: e.target.value }))}
          placeholder="Rechercher une clé..."
          className={inputCls}
        />
      </div>

      {loading ? (
        <div className="flex justify-center py-14">
          <Loader2 className="h-6 w-6 animate-spin text-brand" />
        </div>
      ) : (
        <>
        <BulkDeleteBar count={bulk.selected.length} onDelete={bulk.bulkDelete} testId="bulk-bar-licenses" />
        <TableWrap>
          <table className="w-full min-w-[760px]">
            <thead>
              <tr className="border-b border-line text-left">
                <Th className="w-9">
                  <SelectCheckbox testId="bulk-all-licenses"
                    checked={items.length > 0 && bulk.selected.length >= items.length}
                    onChange={() => bulk.toggleAll(items.map((l) => l.id))} />
                </Th>
                <Th>Clé</Th>
                <Th>Produit</Th>
                <Th>Statut</Th>
                <Th>Commande</Th>
                <Th>Ajoutée le</Th>
                <Th className="text-right">Actions</Th>
              </tr>
            </thead>
            <tbody data-testid="admin-licenses-table">
              {items.map((license) => (
                <tr key={license.id} className="border-b border-line last:border-0">
                  <Td>
                    <SelectCheckbox testId={`bulk-license-${license.id}`}
                      checked={bulk.selected.includes(license.id)} onChange={() => bulk.toggle(license.id)} />
                  </Td>
                  <Td className="font-mono text-[12.5px] font-bold text-brand">{license.key_value}</Td>
                  <Td className="text-slate-500">{license.product_name}</Td>
                  <Td>
                    <Pill
                      tone={
                        license.status === "available"
                          ? "green"
                          : license.status === "sold"
                            ? "blue"
                            : license.status === "reserved"
                              ? "amber"
                              : "rose"
                      }
                    >
                      {LICENSE_STATUS[license.status]?.label || license.status}
                    </Pill>
                  </Td>
                  <Td className="text-slate-500">{license.order_number || "—"}</Td>
                  <Td className="text-slate-500">{formatDate(license.created_at)}</Td>
                  <Td className="text-right">
                    <span className="flex items-center justify-end gap-2">
                      <select
                        data-testid={`license-status-select-${license.id}`}
                        value={license.status}
                        onChange={(e) => changeStatus(license, e.target.value)}
                        className="h-8 rounded-lg border border-line px-2 text-xs font-semibold"
                      >
                        {STATUSES.map((s) => (
                          <option key={s} value={s}>{LICENSE_STATUS[s].label}</option>
                        ))}
                      </select>
                      <button
                        data-testid={`license-delete-${license.id}`}
                        onClick={() => remove(license)}
                        className="flex h-8 w-8 items-center justify-center rounded-lg border border-line text-slate-500 hover:border-rose-300 hover:text-rose-600"
                      >
                        <Trash2 className="h-3.5 w-3.5" />
                      </button>
                    </span>
                  </Td>
                </tr>
              ))}
            </tbody>
          </table>
        </TableWrap>
        </>
      )}

      <AdminModal open={open} onOpenChange={setOpen} title="Importer des licences en quantité">
        <form onSubmit={submitImport} className="space-y-4" data-testid="license-import-form">
          <Field label="Produit">
            <select
              data-testid="license-import-product"
              required
              value={importForm.product_id}
              onChange={(e) => setImportForm((f) => ({ ...f, product_id: e.target.value }))}
              className={inputCls}
            >
              {products.map((p) => (
                <option key={p.id} value={p.id}>{p.name}</option>
              ))}
            </select>
          </Field>
          <Field label="Clés (une par ligne, ou séparées par des virgules)">
            <textarea
              data-testid="license-import-keys"
              required
              rows={8}
              value={importForm.keys}
              onChange={(e) => setImportForm((f) => ({ ...f, keys: e.target.value }))}
              placeholder={"XXXXX-XXXXX-XXXXX-XXXXX\nYYYYY-YYYYY-YYYYY-YYYYY"}
              className={`${textareaCls} font-mono`}
            />
          </Field>
          <Field label="Note interne (lot, fournisseur...)">
            <input
              data-testid="license-import-note"
              value={importForm.note}
              onChange={(e) => setImportForm((f) => ({ ...f, note: e.target.value }))}
              className={inputCls}
            />
          </Field>
          <div className="flex justify-end gap-2 border-t border-line pt-4">
            <button type="button" onClick={() => setOpen(false)} className="rounded-lg border border-line px-4 py-2.5 text-sm font-bold text-ink">
              Annuler
            </button>
            <button
              type="submit"
              data-testid="license-import-submit"
              disabled={importing}
              className="flex items-center gap-1.5 rounded-lg bg-brand px-5 py-2.5 text-sm font-bold text-white disabled:opacity-60"
            >
              {importing ? <Loader2 className="h-4 w-4 animate-spin" /> : <Upload className="h-4 w-4" />} Importer
            </button>
          </div>
        </form>
      </AdminModal>
    </AdminCard>
  );
}
